import * as io from "io-ts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGet } from "../useCall";
import { useCallback, useMemo } from "react";
import { useChannel } from "../ws/useChannel";
import type { Input } from "../call";
import { uniq } from "diagnostics-lib/array";
import { groupBy, indexBy } from "diagnostics-lib/iterable";
import { getFaultEventStatus, type FaultEventStatus } from "diagnostics-domain/faultEvent";
import { uuid } from "diagnostics-lib/io";

const FaultEventHistoriesRouteParams = io.strict({
  projectId: uuid,
  faultEventId: uuid,
  failureNames: io.array(io.string),
});

const ManualFaultEventHistoriesRouteParams = io.strict({
  projectId: io.string,
  manualFaultEventId: uuid,
  manualFaultId: uuid,
});

type BaseHistoryEvent = {
  id: string;
  name: string;
  startsAt: string;
  endsAt: string;
  status: FaultEventStatus;
};
type FaultHistoryEvent = BaseHistoryEvent & {
  detection: boolean | null;
  faultEventFailures: { failureName: string }[];
};
type ManualHistoryEvent = BaseHistoryEvent & {
  id: string;
  manualFailureName?: undefined;
};

type BaseHistoryGroup = {
  projectId: string;
  projectName: string;
  siteId: string | null;
  typeId: string | null;
};

export type FaultHistoryGroup = BaseHistoryGroup & {
  kind: "fault";
  events: FaultHistoryEvent[];
};

export type ManualHistoryGroup = BaseHistoryGroup & {
  kind: "manual";
  events: ManualHistoryEvent[];
};

export type HistoryGroup = FaultHistoryGroup | ManualHistoryGroup;

export const useEventHistories = (
  params:
    | (Input<
        "GET",
        "/projects/:projectId(uuid)/fault-events/:faultEventId(uuid)/history"
      >["routeParams"] &
        Input<"GET", "/projects/:projectId(uuid)/fault-events/:faultEventId(uuid)/history">["body"])
    | Input<
        "GET",
        "/projects/:projectId(uuid)/manual-fault-event/:manualFaultEventId/:manualFaultId/history"
      >["routeParams"],
) => {
  const queryClient = useQueryClient();
  const listFaultEventHistories = useGet(
    "/projects/:projectId(uuid)/fault-events/:faultEventId(uuid)/history",
  );
  const listManualFaultEventHistories = useGet(
    "/projects/:projectId(uuid)/manual-fault-event/:manualFaultEventId/:manualFaultId/history",
  );

  const isFaultEventHistoriesParams = FaultEventHistoriesRouteParams.is(params);
  const isManualFaultEventHistoriesParams = ManualFaultEventHistoriesRouteParams.is(params);
  const key = isFaultEventHistoriesParams
    ? "fault-event"
    : isManualFaultEventHistoriesParams
      ? "manual-fault-event"
      : null;

  const faultEventHistoryResult = useQuery({
    enabled: isFaultEventHistoriesParams,
    queryKey: ["fault-event", "histories", ...Object.entries(params).flat()],
    queryFn: () => {
      if (isFaultEventHistoriesParams) {
        return listFaultEventHistories({
          routeParams: {
            projectId: params.projectId,
            faultEventId: params.faultEventId,
          },
          body: {
            failureNames: params.failureNames,
          },
        });
      }
      return;
    },
    suspense: true,
  });

  const manualFaultEventHistoryResult = useQuery({
    enabled: isManualFaultEventHistoriesParams,
    queryKey: ["manual-fault-event", "histories", ...Object.entries(params).flat()],
    queryFn: () => {
      if (isManualFaultEventHistoriesParams) {
        return listManualFaultEventHistories({ routeParams: params });
      }
      return;
    },
    suspense: true,
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [key, "histories", ...Object.entries(params).flat()],
    });
  }, [key, params, queryClient]);

  useChannel(
    {
      key: "manual_fault_events_history",
      manualFaultId: isManualFaultEventHistoriesParams ? params.manualFaultId : "",
    },
    invalidate,
    true,
    isManualFaultEventHistoriesParams,
  );
  useChannel(
    {
      key: "fault_events_history",
      failureNames: isFaultEventHistoriesParams ? params.failureNames : [],
    },
    invalidate,
    true,
    isFaultEventHistoriesParams,
  );

  const faultEventHistories = useMemo(() => {
    const result: HistoryGroup[] = [];
    if (faultEventHistoryResult.data?.body && isFaultEventHistoriesParams) {
      const faultEventsHistory = faultEventHistoryResult.data.body;
      const projectsById = indexBy(
        uniq(
          faultEventsHistory.map(m => ({
            id: m.id,
            projectId: m.projectId,
            name: m.projectName,
            siteId: m.siteId,
            typeId: m.typeId,
          })),
        ),
        "projectId",
      );

      const groupedFaultEvents = groupBy(faultEventsHistory, "projectId");
      for (const [projectId, histories] of groupedFaultEvents) {
        const project = projectsById.getOrThrow(projectId);

        const events: FaultHistoryEvent[] = [];
        for (const history of histories) {
          const openedAt = history.openedAt ? new Date(history.openedAt) : null;
          const closedAt = history.closedAt ? new Date(history.closedAt) : null;

          const { projectId, projectName, siteId, typeId, updatedAt, ...rest } = history;

          events.push({
            ...rest,
            detection: history.detection ?? null,
            status: getFaultEventStatus(
              {
                openedAt,
                closedAt,
                identifiedAction: history.identifiedAction,
              },
              [],
            ),
          });
        }

        result.push({
          kind: "fault",
          projectId,
          projectName: project.name,
          siteId: project.siteId,
          typeId: project.typeId,
          events,
        });
      }
    }
    return result;
  }, [faultEventHistoryResult.data?.body, isFaultEventHistoriesParams]);

  return useMemo(() => {
    const manualHistories: ManualHistoryGroup[] = (
      manualFaultEventHistoryResult.data?.body ?? []
    ).map(history => ({
      kind: "manual",
      projectId: history.projectId,
      projectName: history.projectName,
      siteId: history.siteId ?? null,
      typeId: history.typeId ?? null,
      events: history.events.map(event => ({
        id: event.id,
        name: event.name ?? event.manualFaultName ?? null,
        startsAt: event.startsAt,
        endsAt: event.endsAt,
        status: event.status as FaultEventStatus,
        detection: null,
      })),
    }));

    return [...faultEventHistories, ...manualHistories];
  }, [faultEventHistories, manualFaultEventHistoryResult]);
};

export type EventHistory = ReturnType<typeof useEventHistories>[number];
